import React, { useState } from 'react';
import { Eye, Trash2, Plus, Search, Filter, Calendar, X, Check, AlertCircle } from 'lucide-react';
import '../../assests/css/admin/Invoices.css';

const Invoices = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ type: '', message: '', title: '' });
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [viewInvoice, setViewInvoice] = useState(null);
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    amount: '',
    description: '',
    dueDate: ''
  });

  const [invoicesData, setInvoicesData] = useState([
    {
      id: '#INV-2024-001',
      customer: 'John Doe',
      email: 'john.doe@example.com',
      date: 'Oct 24, 2023',
      amount: '$120.50',
      status: 'paid',
      description: 'Website design and development services'
    },
    {
      id: '#INV-2024-002',
      customer: 'Jane Smith',
      email: 'jane.smith@design.co',
      date: 'Oct 23, 2023',
      amount: '$450.00',
      status: 'pending',
      description: 'Logo design and branding package'
    },
    {
      id: '#INV-2024-003',
      customer: 'Acme Corp',
      email: 'billing@acmecorp.com',
      date: 'Oct 22, 2023',
      amount: '$1,200.00',
      status: 'overdue',
      description: 'Monthly consulting services'
    },
    {
      id: '#INV-2024-004',
      customer: 'Robert Brown',
      email: 'robert.b@techstyle.io',
      date: 'Oct 21, 2023',
      amount: '$89.99',
      status: 'paid',
      description: 'SEO optimization services'
    },
    {
      id: '#INV-2024-005',
      customer: 'Emily White',
      email: 'emily.w@studio.net',
      date: 'Oct 20, 2023',
      amount: '$210.00',
      status: 'failed',
      description: 'Social media marketing campaign'
    }
  ]);

  const tabs = [
    { key: 'all', label: 'All Invoices', count: invoicesData.length },
    { key: 'paid', label: 'Paid', count: invoicesData.filter(i => i.status === 'paid').length },
    { key: 'pending', label: 'Pending', count: invoicesData.filter(i => i.status === 'pending').length },
    { key: 'overdue', label: 'Overdue', count: invoicesData.filter(i => i.status === 'overdue').length },
    { key: 'failed', label: 'Failed', count: invoicesData.filter(i => i.status === 'failed').length }
  ];

  const showAlertMessage = (type, title, message) => {
    setAlertConfig({ type, title, message });
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const handleCreateInvoice = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      customerName: '',
      customerEmail: '',
      amount: '',
      description: '',
      dueDate: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.customerName || !formData.customerEmail || !formData.amount) {
      showAlertMessage('error', 'Error!', 'Please fill in all required fields');
      return;
    }

    const newInvoice = {
      id: `#INV-2024-${String(invoicesData.length + 1).padStart(3, '0')}`,
      customer: formData.customerName,
      email: formData.customerEmail,
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      amount: `$${formData.amount}`,
      status: 'pending',
      description: formData.description
    };

    setInvoicesData([newInvoice, ...invoicesData]);
    handleCloseModal();
    showAlertMessage('success', 'Success!', 'Invoice created successfully!');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleView = (invoice) => {
    setViewInvoice(invoice);
  };

  const handleCloseView = () => {
    setViewInvoice(null);
  };

  const handleDelete = (invoice) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete invoice ${invoice.id}?`);
    if (confirmDelete) {
      setInvoicesData(invoicesData.filter(inv => inv.id !== invoice.id));
      showAlertMessage('success', 'Deleted!', `Invoice ${invoice.id} has been deleted successfully.`);
    }
  };

  const handleBulkDelete = () => {
    if (selectedInvoices.length === 0) {
      showAlertMessage('error', 'Error!', 'Please select invoices to delete');
      return;
    }

    const confirmDelete = window.confirm(`Are you sure you want to delete ${selectedInvoices.length} invoice(s)?`);
    if (confirmDelete) {
      setInvoicesData(invoicesData.filter(inv => !selectedInvoices.includes(inv.id)));
      setSelectedInvoices([]);
      showAlertMessage('success', 'Deleted!', `${selectedInvoices.length} invoice(s) deleted successfully.`);
    }
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => {
      if (prev.includes(invoiceId)) {
        return prev.filter(id => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedInvoices(filteredInvoices.map(inv => inv.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const getStatusClass = (status) => {
    const classes = {
      paid: 'Admin-invoices-AdminInvoice-status-paid',
      pending: 'Admin-invoices-AdminInvoice-status-pending',
      overdue: 'Admin-invoices-AdminInvoice-status-overdue',
      failed: 'Admin-invoices-AdminInvoice-status-failed'
    };
    return classes[status] || '';
  };

  const filteredInvoices = invoicesData.filter(invoice => {
    const matchesSearch = invoice.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         invoice.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         invoice.id.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesTab = activeTab === 'all' || invoice.status === activeTab;
    const matchesFilter = filterStatus === 'all' || invoice.status === filterStatus;
    
    let matchesDate = true;
    if (selectedDate) {
      const invoiceDate = new Date(invoice.date);
      const filterDate = new Date(selectedDate);
      matchesDate = invoiceDate.toDateString() === filterDate.toDateString();
    }
    
    return matchesSearch && matchesTab && matchesFilter && matchesDate;
  });

  return (
    <div className="Admin-invoices-AdminInvoice-container">
      {/* Alert Message */}
      {showAlert && (
        <div className={`Admin-invoices-AdminInvoice-alert-message ${alertConfig.type}`}>
          <div className="Admin-invoices-AdminInvoice-alert-icon">
            {alertConfig.type === 'success' && <Check size={24} />}
            {alertConfig.type === 'error' && <X size={24} />}
            {alertConfig.type === 'info' && <AlertCircle size={24} />}
          </div>
          <div className="Admin-invoices-AdminInvoice-alert-content">
            <h3>{alertConfig.title}</h3>
            <p>{alertConfig.message}</p>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {viewInvoice && (
        <div className="Admin-invoices-AdminInvoice-modal-overlay" onClick={handleCloseView}>
          <div className="Admin-invoices-AdminInvoice-view-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="Admin-invoices-AdminInvoice-modal-header">
              <h2>Invoice Details</h2>
              <button className="Admin-invoices-AdminInvoice-modal-close" onClick={handleCloseView}>
                <X size={24} />
              </button>
            </div>
            
            <div className="Admin-invoices-AdminInvoice-invoice-details">
              <div className="Admin-invoices-AdminInvoice-detail-row">
                <span className="Admin-invoices-AdminInvoice-detail-label">Invoice ID:</span>
                <span className="Admin-invoices-AdminInvoice-detail-value">{viewInvoice.id}</span>
              </div>
              <div className="Admin-invoices-AdminInvoice-detail-row">
                <span className="Admin-invoices-AdminInvoice-detail-label">Customer Name:</span>
                <span className="Admin-invoices-AdminInvoice-detail-value">{viewInvoice.customer}</span>
              </div>
              <div className="Admin-invoices-AdminInvoice-detail-row">
                <span className="Admin-invoices-AdminInvoice-detail-label">Email:</span>
                <span className="Admin-invoices-AdminInvoice-detail-value">{viewInvoice.email}</span>
              </div>
              <div className="Admin-invoices-AdminInvoice-detail-row">
                <span className="Admin-invoices-AdminInvoice-detail-label">Date Issued:</span>
                <span className="Admin-invoices-AdminInvoice-detail-value">{viewInvoice.date}</span>
              </div>
              <div className="Admin-invoices-AdminInvoice-detail-row">
                <span className="Admin-invoices-AdminInvoice-detail-label">Amount:</span>
                <span className="Admin-invoices-AdminInvoice-detail-value Admin-invoices-AdminInvoice-amount-highlight">{viewInvoice.amount}</span>
              </div>
              <div className="Admin-invoices-AdminInvoice-detail-row">
                <span className="Admin-invoices-AdminInvoice-detail-label">Status:</span>
                <span className={`Admin-invoices-AdminInvoice-status-badge ${getStatusClass(viewInvoice.status)}`}>
                  {viewInvoice.status.charAt(0).toUpperCase() + viewInvoice.status.slice(1)}
                </span>
              </div>
              <div className="Admin-invoices-AdminInvoice-detail-row Admin-invoices-AdminInvoice-detail-full">
                <span className="Admin-invoices-AdminInvoice-detail-label">Description:</span>
                <p className="Admin-invoices-AdminInvoice-detail-description">{viewInvoice.description || 'No description provided'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showModal && (
        <div className="Admin-invoices-AdminInvoice-modal-overlay" onClick={handleCloseModal}>
          <div className="Admin-invoices-AdminInvoice-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="Admin-invoices-AdminInvoice-modal-header">
              <h2>Create New Invoice</h2>
              <button className="Admin-invoices-AdminInvoice-modal-close" onClick={handleCloseModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="Admin-invoices-AdminInvoice-form-group">
                <label>Customer Name *</label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  placeholder="Enter customer name"
                  required
                />
              </div>

              <div className="Admin-invoices-AdminInvoice-form-group">
                <label>Customer Email *</label>
                <input
                  type="email"
                  name="customerEmail"
                  value={formData.customerEmail}
                  onChange={handleInputChange}
                  placeholder="customer@example.com"
                  required
                />
              </div>

              <div className="Admin-invoices-AdminInvoice-form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  required
                />
              </div>

              <div className="Admin-invoices-AdminInvoice-form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter invoice description..."
                  rows="3"
                />
              </div>

              <div className="Admin-invoices-AdminInvoice-form-group">
                <label>Due Date</label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleInputChange}
                />
              </div>

              <div className="Admin-invoices-AdminInvoice-modal-actions">
                <button type="button" className="Admin-invoices-AdminInvoice-btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="Admin-invoices-AdminInvoice-btn-submit">
                  Create Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="Admin-invoices-AdminInvoice-header">
        <div className="Admin-invoices-AdminInvoice-header-content">
          <h1 className="Admin-invoices-AdminInvoice-page-title">Invoices List</h1>
          <p className="Admin-invoices-AdminInvoice-page-subtitle">Manage, view, and send invoices to your customers.</p>
        </div>
        <div className="Admin-invoices-AdminInvoice-header-actions">
          {selectedInvoices.length > 0 && (
            <button className="Admin-invoices-AdminInvoice-btn-delete-bulk" onClick={handleBulkDelete}>
              <Trash2 size={18} />
              Delete Selected ({selectedInvoices.length})
            </button>
          )}
          <button className="Admin-invoices-AdminInvoice-btn-create" onClick={handleCreateInvoice}>
            <Plus size={18} />
            Create New Invoice
          </button>
        </div>
      </div>

      <div className="Admin-invoices-AdminInvoice-filters-section">
        <div className="Admin-invoices-AdminInvoice-search-box">
          <Search className="Admin-invoices-AdminInvoice-search-icon" size={20} />
          <input
            type="text"
            placeholder="Search by Invoice ID, Customer name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="Admin-invoices-AdminInvoice-filter-dropdown">
          <Filter size={18} />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <div className="Admin-invoices-AdminInvoice-date-picker">
          <Calendar size={18} />
          <input 
            type="date" 
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </div>

      <div className="Admin-invoices-AdminInvoice-tabs-container">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`Admin-invoices-AdminInvoice-tab ${activeTab === tab.key ? 'Admin-invoices-AdminInvoice-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            <span className="Admin-invoices-AdminInvoice-tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      <div className="Admin-invoices-AdminInvoice-table">
        <table>
          <thead>
            <tr>
              <th>
                <input 
                  type="checkbox" 
                  onChange={handleSelectAll}
                  checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0}
                />
              </th>
              <th>INVOICE ID</th>
              <th>CUSTOMER</th>
              <th>DATE ISSUED</th>
              <th>AMOUNT</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.length > 0 ? (
              filteredInvoices.map((invoice, index) => (
                <tr key={invoice.id} style={{ animationDelay: `${index * 0.1}s` }}>
                  <td>
                    <input 
                      type="checkbox" 
                      checked={selectedInvoices.includes(invoice.id)}
                      onChange={() => handleSelectInvoice(invoice.id)}
                    />
                  </td>
                  <td>
                    <span className="Admin-invoices-AdminInvoice-invoice-id">{invoice.id}</span>
                  </td>
                  <td>
                    <div className="Admin-invoices-AdminInvoice-customer-info">
                      <span className="Admin-invoices-AdminInvoice-customer-name">{invoice.customer}</span>
                      <span className="Admin-invoices-AdminInvoice-customer-email">{invoice.email}</span>
                    </div>
                  </td>
                  <td>{invoice.date}</td>
                  <td className="Admin-invoices-AdminInvoice-amount">{invoice.amount}</td>
                  <td>
                    <span className={`Admin-invoices-AdminInvoice-status-badge ${getStatusClass(invoice.status)}`}>
                      {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div className="Admin-invoices-AdminInvoice-action-buttons">
                      <button className="Admin-invoices-AdminInvoice-action-btn" title="View" onClick={() => handleView(invoice)}>
                        <Eye size={18} />
                      </button>
                      <button className="Admin-invoices-AdminInvoice-action-btn Admin-invoices-AdminInvoice-delete-btn" title="Delete" onClick={() => handleDelete(invoice)}>
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#6b7280', fontSize: '16px' }}>No invoices found</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Invoices;